<template>
  <div class="spotting-area-map-view">
    <!-- Loading State -->
    <div v-if="isLoading" class="map-loading-state">
      <div class="loading-spinner">
        <icon name="loading"/>
      </div>
      <p class="loading-text">{{ loadingMessage }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="hasError" class="map-error-state">
      <div class="error-icon">
        <icon name="alert-triangle"/>
      </div>
      <p class="error-message">{{ errorMessage }}</p>
      <button
        v-if="canRetry"
        @click="retryMapLoad"
        class="retry-button"
        :disabled="isRetrying"
      >
        <icon v-if="isRetrying" name="loading"/>
        {{ isRetrying ? 'Retrying...' : 'Retry' }}
      </button>
    </div>

    <!-- Map Component -->
    <div v-else class="map-container">
      <map-view
        :key="mapKey"
        ref="mapComponent"
        :data="data"
        :getMapData="getMapData"
        :getData="getData"
        :no-search-while-moving="noSearchWhileMoving"
        :disable-mouse-wheel-zoom="disableMouseWheelZoom"
        :disable-expand-button="disableExpandButton"
        :custom-center="computedMapCenter"
        :custom-zoom="computedMapZoom"
        v-bind="$attrs"
        @selectedLat="handleCoordinateUpdate"
        @map-ready="handleMapReady"
        @map-error="handleMapError"
      />
    </div>
  </div>
</template>

<script>
  import MapView from './MapView.vue'
  import { defineComponent } from 'vue'

  export default defineComponent({
    name: 'SpottingAreaMapView',

    components: {
      MapView
    },

    props: {
      // Spotting area data containing map coordinates
      spottingArea: {
        type: Object,
      },

      // Map data for markers/overlays
      data: {
        type: [Array, Object],
        default: () => []
      },

      // Map data fetching functions
      getMapData: {
        type: Function,
        required: true
      },

      getData: {
        type: Function,
        default: null
      },

      // Map configuration
      noSearchWhileMoving: {
        type: Boolean,
        default: true
      },

      disableMouseWheelZoom: {
        type: Boolean,
        default: true
      },

      disableExpandButton: {
        type: Boolean,
        default: false
      },

      // Override coordinates (for settings page click-to-set functionality)
      customCenter: {
        type: Array,
        default: null,
        validator: (value) => {
          if (!value) return true
          return Array.isArray(value) && value.length === 2 &&
            value.every(coord => typeof coord === 'number' && !isNaN(coord))
        }
      },

      customZoom: {
        type: Number,
        default: null,
        validator: (value) => {
          if (value === null) return true
          return typeof value === 'number' && !isNaN(value) && value >= 1 && value <= 20
        }
      },

      // External refresh trigger
      refreshTrigger: {
        type: Number,
        default: 0
      },

      // Loading message override
      loadingMessage: {
        type: String,
        default: 'Loading map...'
      }
    },

    emits: ['selectedLat', 'mapReady', 'mapError', 'coordinatesReady'],

    data () {
      return {
        mapKey: 1,
        isMapInitialized: false,
        hasError: false,
        errorMessage: '',
        canRetry: true,
        isRetrying: false,
        retryCount: 0,
        maxRetries: 3,
        loadingTimeout: null
      }
    },

    computed: {
      isLoading () {
        // Show loading if:
        // 1. No spotting area data at all
        // 2. Spotting area exists but doesn't have coordinate data yet
        // 3. Map is not initialized and we're expecting coordinates

        if (!this.spottingArea && !this.customCenter) {
          return true
        }

        if (this.spottingArea && !this.hasCoordinateData && !this.customCenter) {
          return true
        }

        return false
      },

      hasCoordinateData () {
        if (!this.spottingArea) return false

        // Check for both naming conventions
        const hasSnakeCase = this.spottingArea.hasOwnProperty('map_latitude') &&
          this.spottingArea.hasOwnProperty('map_longitude')
        const hasCamelCase = this.spottingArea.hasOwnProperty('mapLatitude') &&
          this.spottingArea.hasOwnProperty('mapLongitude')

        return hasSnakeCase || hasCamelCase
      },

      computedMapCenter () {
        // Priority: customCenter > spottingArea coordinates > null
        if (this.customCenter && Array.isArray(this.customCenter) && this.customCenter.length === 2) {
          return this.customCenter
        }

        if (!this.spottingArea) return null

        // Handle both naming conventions: snake_case and camelCase
        const lat = this.spottingArea.map_latitude || this.spottingArea.mapLatitude
        const lng = this.spottingArea.map_longitude || this.spottingArea.mapLongitude

        if (lat != null && lng != null) {
          const parsedLat = parseFloat(lat)
          const parsedLng = parseFloat(lng)

          if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
            return [parsedLat, parsedLng]
          }
        }

        return null
      },

      computedMapZoom () {
        // Priority: customZoom > spottingArea zoom > default (8)
        if (this.customZoom !== null && !isNaN(this.customZoom)) {
          return parseInt(this.customZoom)
        }

        if (!this.spottingArea) return 8

        // Handle both naming conventions: snake_case and camelCase
        const zoom = this.spottingArea.map_zoom || this.spottingArea.mapZoom

        if (zoom != null && !isNaN(zoom)) {
          return parseInt(zoom)
        }

        return 8 // Default zoom
      }
    },

    watch: {
      // Watch for changes in spotting area and refresh map accordingly
      spottingArea: {
        handler (newSpottingArea, oldSpottingArea) {
          this.clearError()

          if (!newSpottingArea) return

          // Check if coordinates actually changed
          const oldCenter = this.extractCoordinates(oldSpottingArea)
          const newCenter = this.extractCoordinates(newSpottingArea)
          const oldZoom = this.extractZoom(oldSpottingArea)
          const newZoom = this.extractZoom(newSpottingArea)

          // Only refresh if coordinates or zoom actually changed
          if (JSON.stringify(oldCenter) !== JSON.stringify(newCenter) || oldZoom !== newZoom) {
            this.refreshMap()
          }
        },
        deep: true,
        immediate: false
      },

      // Watch for external refresh trigger
      refreshTrigger (newValue, oldValue) {
        if (newValue !== oldValue && newValue > 0) {
          this.refreshMap()
        }
      },

      // Watch for custom coordinates changes (for settings page)
      customCenter: {
        handler () {
          this.clearError()
          this.refreshMap()
        },
        deep: true
      },

      customZoom () {
        this.refreshMap()
      },

      // Emit when coordinates are ready
      computedMapCenter: {
        handler (newCenter) {
          if (newCenter) {
            this.$emit('coordinatesReady', {
              center: newCenter,
              zoom: this.computedMapZoom
            })
          }
        },
        immediate: true
      }
    },

    methods: {
      refreshMap () {
        return

        /*this.clearError()
        this.mapKey += 1

        // Set a timeout for loading state
        this.setLoadingTimeout()

        this.$nextTick(() => {
          this.isMapInitialized = false

          // Additional wait for map to fully initialize
          setTimeout(() => {
            this.invalidateMapSize()
          }, 100)
        })*/
      },

      invalidateMapSize () {
        if (this.$refs.mapComponent) {
          try {
            if (typeof this.$refs.mapComponent.invalidateSize === 'function') {
              this.$refs.mapComponent.invalidateSize()
            }
            if (typeof this.$refs.mapComponent.refreshMap === 'function') {
              this.$refs.mapComponent.refreshMap()
            }
          } catch (error) {
            console.warn('Failed to invalidate map size:', error)
            this.handleMapError('Failed to refresh map display')
          }
        }
      },

      handleCoordinateUpdate (coordinateData) {
        this.$emit('selectedLat', coordinateData)
      },

      handleMapReady () {
        this.clearLoadingTimeout()
        this.isMapInitialized = true
        this.$emit('mapReady')
      },

      handleMapError (error) {
        this.clearLoadingTimeout()
        this.hasError = true
        this.errorMessage = typeof error === 'string' ? error : 'Failed to load map. Please try again.'
        this.canRetry = this.retryCount < this.maxRetries
        this.$emit('mapError', error)
      },

      retryMapLoad () {
        if (this.retryCount >= this.maxRetries) return

        this.isRetrying = true
        this.retryCount += 1

        setTimeout(() => {
          this.clearError()
          this.refreshMap()
          this.isRetrying = false
        }, 1000)
      },

      clearError () {
        this.hasError = false
        this.errorMessage = ''
        this.isRetrying = false
      },

      setLoadingTimeout () {
        this.clearLoadingTimeout()
        // Set a timeout to show error if map doesn't load within 10 seconds
        this.loadingTimeout = setTimeout(() => {
          if (!this.isMapInitialized && !this.hasError) {
            this.handleMapError('Map loading timeout. Please try again.')
          }
        }, 10000)
      },

      clearLoadingTimeout () {
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout)
          this.loadingTimeout = null
        }
      },

      extractCoordinates (spottingArea) {
        if (!spottingArea) return null

        const lat = spottingArea.map_latitude || spottingArea.mapLatitude
        const lng = spottingArea.map_longitude || spottingArea.mapLongitude

        if (lat != null && lng != null) {
          return [parseFloat(lat), parseFloat(lng)]
        }
        return null
      },

      extractZoom (spottingArea) {
        if (!spottingArea) return 8

        const zoom = spottingArea.map_zoom || spottingArea.mapZoom
        return zoom != null ? parseInt(zoom) : 8
      },

      // Public methods for external control
      forceRefresh () {
        this.retryCount = 0
        this.refreshMap()
      },

      invalidateSize () {
        this.invalidateMapSize()
      }
    },

    mounted () {
      if (this.computedMapCenter || this.customCenter) {
        this.refreshMap()
      }
    },

    beforeUnmount () {
      this.clearLoadingTimeout()
    }
  })
</script>

<style lang="scss" scoped>
  .spotting-area-map-view {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 200px;
  }

  .map-loading-state,
  .map-error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 200px;
    color: #666;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
  }

  .loading-spinner,
  .error-icon {
    margin-bottom: 12px;

    .svg-icon {
      width: 24px;
      height: 24px;
    }
  }

  .loading-text,
  .error-message {
    margin: 0;
    font-size: 14px;
    text-align: center;
  }

  .error-message {
    color: #dc3545;
    margin-bottom: 16px;
  }

  .retry-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    font-size: 14px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover:not(:disabled) {
      background-color: #0056b3;
    }

    &:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }

    .svg-icon {
      width: 16px;
      height: 16px;
    }
  }

  .map-container {
    width: 100%;
    height: 100%;
    position: relative;
  }
</style>
